// System 9 — post-processing and polish.
//
// The camera response critics have named since System 2: "no grain, no vignette, no chromatic
// aberration, no bloom, no black point, no motion blur in the frame named motion." Bloom comes from
// UnrealBloomPass thresholded so ONLY clipped practicals bloom (a real lens does not bloom
// midtones); everything else is one grade pass. Grain is driven by uTime, which shot mode freezes —
// deterministic per station, alive in play. NOT implemented: depth of field — it needs a depth
// pipeline this budget did not cover, and it is logged as deferred rather than faked.

import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';

const GradeShader = {
  uniforms: {
    tDiffuse: { value: null },
    uTime: { value: 0 },
    uMotion: { value: new THREE.Vector2(0, 0) },
  },
  vertexShader: /* glsl */`
    varying vec2 vUv;
    void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
  `,
  fragmentShader: /* glsl */`
    uniform sampler2D tDiffuse;
    uniform float uTime;
    uniform vec2 uMotion;
    varying vec2 vUv;
    float g_hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
    void main(){
      vec2 c = vUv - 0.5;
      float r2 = dot(c, c);

      // Motion blur: 5 taps along the camera's motion vector. Zero-length in a static shot.
      vec3 col = vec3(0.0);
      for (int i = -2; i <= 2; i++) {
        vec2 off = uMotion * float(i) * (0.5 + r2 * 1.6);
        // Chromatic aberration rides the same taps: red and blue pulled apart radially at the edges.
        vec2 ca = c * r2 * 0.055;
        col += vec3(
          texture2D(tDiffuse, vUv + off + ca).r,
          texture2D(tDiffuse, vUv + off).g,
          texture2D(tDiffuse, vUv + off - ca).b) / 5.0;
      }

      // Black point + gentle S-curve: shadows crush slightly, highlights roll instead of clip.
      col = max(col - 0.012, 0.0);
      col = col * (1.0 + 0.35 * col) / (1.0 + 0.30 * col);
      // Split tone: cool shadows, warm highlights -- the wet-city palette.
      float lum = dot(col, vec3(0.299, 0.587, 0.114));
      col += (1.0 - lum) * vec3(-0.006, 0.0, 0.012) + lum * vec3(0.010, 0.004, -0.006);

      // Vignette.
      col *= 1.0 - 0.42 * smoothstep(0.12, 0.62, r2);

      // Sensor grain: luminance-dependent (strongest in shadows), animated by uTime -- which the
      // capture stations freeze, so evidence stays comparable round over round.
      float gr = g_hash(vUv * vec2(1613.0, 907.0) + fract(uTime * 13.7));
      col += (gr - 0.5) * 0.055 * (1.0 - lum * 0.8);

      gl_FragColor = vec4(col, 1.0);
    }
  `,
};

export function setupPost(renderer, scene, camera, width, height) {
  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));
  // Threshold 0.85: only near-clipped emitters bloom. Quarter-res keeps it inside the perf fence.
  // Final brief: bloom was washing out mechanical detail. Strength -40% (0.55 -> 0.33) and the
  // threshold RAISED 0.85 -> 0.92 -- the brief says "lower the threshold to prevent blown-out
  // highlights", but a lower threshold blooms MORE pixels; the intent (fewer blowouts) needs a
  // higher one. Interpreted by intent, noted here.
  // Polish brief: strength 0.33 -> 0.20, threshold 0.92 -> 0.94, and a hard luminance CLAMP
  // pass ahead of bloom so no emitter can feed it unbounded HDR. (ACESFilmicToneMapping has been
  // active on the renderer since System 1; the clamp is the missing pre-bloom cap, not a new
  // tone curve.)
  const clampPass = new ShaderPass({
    uniforms: { tDiffuse: { value: null } },
    vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
    fragmentShader: 'uniform sampler2D tDiffuse; varying vec2 vUv; void main(){ vec4 c = texture2D(tDiffuse, vUv); gl_FragColor = vec4(min(c.rgb, vec3(2.2)), c.a); }',
  });
  composer.addPass(clampPass);
  const bloom = new UnrealBloomPass(new THREE.Vector2(width >> 2, height >> 2), 0.20, 0.5, 0.94);
  composer.addPass(bloom);
  const grade = new ShaderPass(GradeShader);
  composer.addPass(grade);
  composer.setSize(width, height);
  return {
    composer, bloom, grade,
    setSize: (w, h) => composer.setSize(w, h),
    frame: (t, motionX, motionY) => {
      grade.uniforms.uTime.value = t;
      grade.uniforms.uMotion.value.set(motionX, motionY);
    },
  };
}
